<script>
  import { onMount } from 'svelte';
  import Layout from '../components/Layout.svelte';
  import ErrorBanner from '../components/ErrorBanner.svelte';
  import InfoHint from '../components/InfoHint.svelte';
  import { companiesApi } from '../stores/api.js';
  import { companies, loadCompanies, setActiveCompany } from '../stores/company.js';
  import { isAdmin } from '../stores/roles.js';
  import { push } from 'svelte-spa-router';
  import { t, tImmediate } from '../i18n/index.js';
  import { Building2, Plus, Check, Edit, Trash2 } from '../lib/icons/index.js';

  let showCreateForm = false;
  let loading = true;
  let error = '';

  // Create form fields
  let form = {
    ragioneSociale: '', partitaIva: '', codiceFiscale: '', regimeFiscale: 'RF01',
    codiceDestinatario: '', pecDestinatario: '',
    sede: { indirizzo: '', cap: '', comune: '', provincia: '', nazione: 'IT' },
  };

  onMount(async () => {
    await loadCompanies();
    loading = false;
  });

  async function handleCreate(e) {
    e.preventDefault();
    error = '';
    try {
      const company = await companiesApi.create(form);
      await loadCompanies();
      setActiveCompany(company.id);
      showCreateForm = false;
      form = { ragioneSociale: '', partitaIva: '', codiceFiscale: '', regimeFiscale: 'RF01', codiceDestinatario: '', pecDestinatario: '', sede: { indirizzo: '', cap: '', comune: '', provincia: '', nazione: 'IT' } };
    } catch (err) {
      error = err.message;
    }
  }

  async function handleDelete(id) {
    if (!confirm(tImmediate('companies.confirmDelete'))) return;
    try {
      await companiesApi.remove(id);
      await loadCompanies();
    } catch (err) {
      error = err.message;
    }
  }

  // Regime fiscale codes; labels resolved at render time via $t().
  const regimeFiscaleCodes = ['RF01', 'RF02', 'RF04', 'RF19'];
</script>

<Layout>
  <div class="max-w-4xl mx-auto">
    <div class="flex items-center justify-between mb-2">
      <div class="flex items-center gap-3">
        <h1 class="section-title mb-0">{$t('companies.title')}</h1>
      </div>
      {#if $isAdmin}
        <button on:click={() => showCreateForm = !showCreateForm}
          class="btn btn-primary btn-sm gap-2">
          <Plus size={16} />
          <span>{$t('companies.newCompany')}</span>
        </button>
      {/if}
    </div>
    <p class="mb-6 text-sm" style="color: var(--color-text-muted);">{$t('companies.subtitle')}</p>

    <ErrorBanner {error} />

    {#if showCreateForm}
      <div class="card p-6 mb-6">
        <h2 class="text-lg font-bold mb-4" style="color: var(--color-text-heading);">{$t('companies.newTitle')}</h2>
        <form on:submit={handleCreate} class="space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label for="comp-ragione-sociale" class="block eyebrow mb-1" style="color: var(--color-text-muted);">{$t('companies.fields.ragioneSociale')}</label>
              <input id="comp-ragione-sociale" bind:value={form.ragioneSociale} required class="input" />
            </div>
            <div>
              <label for="comp-partita-iva" class="block eyebrow mb-1" style="color: var(--color-text-muted);">{$t('companies.fields.partitaIva')}</label>
              <input id="comp-partita-iva" bind:value={form.partitaIva} required maxlength="11" class="input" />
            </div>
            <div>
              <label for="comp-codice-fiscale" class="block eyebrow mb-1" style="color: var(--color-text-muted);">{$t('companies.fields.codiceFiscale')}</label>
              <input id="comp-codice-fiscale" bind:value={form.codiceFiscale} maxlength="16" class="input" />
            </div>
            <div>
              <label for="comp-regime-fiscale" class="block eyebrow mb-1" style="color: var(--color-text-muted);">{$t('companies.fields.regimeFiscale')} <InfoHint text={$t('companies.fields.regimeFiscaleHint')} /></label>
              <select id="comp-regime-fiscale" bind:value={form.regimeFiscale} class="input">
                {#each regimeFiscaleCodes as code}
                  <option value={code}>{code} - {$t(`companies.regimi.${code}`)}</option>
                {/each}
              </select>
            </div>
            <div>
              <label for="comp-codice-destinatario" class="block eyebrow mb-1" style="color: var(--color-text-muted);">{$t('companies.fields.codiceDestinatario')} <InfoHint text={$t('companies.fields.codiceDestinatarioHint')} /></label>
              <input id="comp-codice-destinatario" bind:value={form.codiceDestinatario} maxlength="7" class="input" />
            </div>
            <div>
              <label for="comp-pec" class="block eyebrow mb-1" style="color: var(--color-text-muted);">{$t('companies.fields.pec')} <InfoHint text={$t('companies.fields.pecHint')} /></label>
              <input id="comp-pec" bind:value={form.pecDestinatario} type="email" class="input" />
            </div>
          </div>

          <h3 class="eyebrow eyebrow-lg mt-4" style="color: var(--color-text-muted);">{$t('companies.fields.legalAddress')}</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="md:col-span-2">
              <label for="comp-indirizzo" class="block eyebrow mb-1" style="color: var(--color-text-muted);">{$t('companies.fields.address')}</label>
              <input id="comp-indirizzo" bind:value={form.sede.indirizzo} required class="input" />
            </div>
            <div>
              <label for="comp-cap" class="block eyebrow mb-1" style="color: var(--color-text-muted);">{$t('companies.fields.cap')}</label>
              <input id="comp-cap" bind:value={form.sede.cap} required maxlength="5" class="input" />
            </div>
            <div>
              <label for="comp-comune" class="block eyebrow mb-1" style="color: var(--color-text-muted);">{$t('companies.fields.city')}</label>
              <input id="comp-comune" bind:value={form.sede.comune} required class="input" />
            </div>
            <div>
              <label for="comp-provincia" class="block eyebrow mb-1" style="color: var(--color-text-muted);">{$t('companies.fields.province')}</label>
              <input id="comp-provincia" bind:value={form.sede.provincia} maxlength="2" class="input" />
            </div>
            <div>
              <label for="comp-nazione" class="block eyebrow mb-1" style="color: var(--color-text-muted);">{$t('companies.fields.country')}</label>
              <input id="comp-nazione" bind:value={form.sede.nazione} maxlength="2" class="input" />
            </div>
          </div>

          <p class="text-xs pt-2" style="color: var(--color-text-muted);">{$t('common.requiredLegend')}</p>

          <div class="flex gap-3 pt-2">
            <button type="submit" class="btn btn-primary btn-sm">{$t('common.create')}</button>
            <button type="button" on:click={() => showCreateForm = false} class="btn btn-secondary btn-sm">{$t('common.cancel')}</button>
          </div>
        </form>
      </div>
    {/if}

    {#if loading}
      <div class="text-center py-8" style="color: var(--color-text-muted);">{$t('app.loading')}</div>
    {:else if $companies.length === 0}
      <div class="card p-8 text-center">
        <Building2 size={48} class="mx-auto mb-4 opacity-30" style="color: var(--color-text-muted);" />
        <p style="color: var(--color-text-muted);">{$t('companies.empty')}</p>
        {#if $isAdmin}
          <p class="text-sm mt-2" style="color: var(--color-text-muted);">{$t('companies.emptyHint')}</p>
        {/if}
      </div>
    {:else}
      <div class="space-y-3">
        {#each $companies as company}
          <div class="card p-4 flex items-center justify-between">
            <div class="flex-1">
              <div class="flex items-center gap-2">
                <h3 class="font-bold" style="color: var(--color-text-heading);">{company.ragioneSociale}</h3>
              </div>
              <div class="flex gap-4 mt-1 text-xs font-mono" style="color: var(--color-text-muted);">
                <span>{$t('companies.fields.partitaIvaShort')}: {company.partitaIva}</span>
                {#if company.codiceDestinatario}
                  <span>{$t('companies.fields.codiceDestinatarioShort')}: {company.codiceDestinatario}</span>
                {/if}
                <span>{company.regimeFiscale}</span>
              </div>
            </div>
            <div class="flex items-center gap-2">
              <button on:click={() => push(`/companies/${company.id}/settings`)}
                class="p-2 hover:text-action transition-colors" style="color: var(--color-text-muted);" title={$t('companies.settingsHint')} aria-label={$t('companies.actions.settings')}>
                <Edit size={16} />
              </button>
              {#if $isAdmin}
                <button on:click={() => handleDelete(company.id)}
                  class="p-2 hover:text-danger transition-colors" style="color: var(--color-text-muted);" title={$t('companies.actions.delete')} aria-label={$t('companies.actions.delete')}>
                  <Trash2 size={16} />
                </button>
              {/if}
            </div>
          </div>
        {/each}
      </div>
    {/if}
  </div>
</Layout>
